import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useRef, useState } from "react";
import {
  getCitiesForCounty,
  getCombinedList,
} from "../../functions/availablePermitForms";
import { useSelector, useDispatch } from "react-redux";
import { useTour } from "@reactour/tour";
import SelectFormsStep from "./selectPermitPackageSteps/SelectFormsStep";
import SelectContractorStep from "./selectPermitPackageSteps/SelectContractorStep";
import SelectCustomerUserStep from "./selectPermitPackageSteps/SelectCustomerUserStep";
import SetupJaxDetails from "./selectPermitPackageSteps/SetupJaxDetails";
import GeneratorPermitEmailStep from "./selectPermitPackageSteps/GeneratorPermitEmailStep";
import { setAttachingNewPermit } from "../../store/actions/uiActions/myPermitsActions";
import { isSubAdminEmail } from "../../functions/helperFunctions";

const SelectPermitPackageForms = (props) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const permitTypeFormMap = useSelector(
    (state) => state.nocData.permit_type_form_map,
  );
  const displayImageRef = useRef(props?.displayImage); // store previous image
  const projectNameFieldRef = useRef(null);
  const descriptionTextRef = useRef(null);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumDown = useMediaQuery(theme.breakpoints.down("md"));

  const [shouldExpandOnScroll, setShouldExpandOnScroll] = useState(false);
  const [headerCondensed, setHeaderCondensed] = useState(false);
  const [expandedPermitField, setExpandedPermitField] = useState(true);

  const {
    contractors,
    isPP,
    formContractors,
    setFormContractors,
    autoEmailEnabled,
    setAutoEmailEnabled,
    cityEmail,
    setCityEmail,
    responseEmail,
    setResponseEmail,
    emailRows,
    setEmailRows,
    userEmail,
    emailRowOnError,
    setEmailRowOnError,
    selectUserLoading,
    setSelectUserLoading,
    contractorSource,
    setContractorSource,
    selectedCustomerUser,
    setSelectedCustomerUser,
    selectedCustomerContractor,
    setSelectedCustomerContractor,
    setNocId,
    nocId,
    fetchCountyPermit,
  } = props;

  const { isOpen, setIsOpen } = useTour();

  const availablePermitForms = useSelector(
    (state) => state.nocData?.available_permit_forms,
  );
  const { subAdmins } = useSelector((state) => state.nocData || {});
  const { subAdminDomains = [] } = subAdmins || {};
  const availablePermitTypes = useSelector(
    (state) => state.nocData?.available_permit_types,
  );
  const formsOrder = useSelector((state) => state.nocData?.nocData?.formOrders);
  const selectedFormsRef = useRef(props?.selectedForms);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const attachingNewPermit = useSelector(
    (state) => state.myPermitsComponent?.attachingNewPermit,
  );
  const isAdmin = currentUser?.user?.role === "admin";
  const isSubAdmin = isSubAdminEmail(currentUser?.user?.email, subAdminDomains);
  const canCreateAsCustomer = isAdmin || isSubAdmin;

  useEffect(() => {
    displayImageRef.current = props?.displayImage;
  }, [props?.displayImage]);

  useEffect(() => {
    if (!isMobile) {
      setHeaderCondensed(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMediumDown) setExpandedPermitField(true);
  }, [isMediumDown]);

  useEffect(() => {
    if (shouldExpandOnScroll) setExpandedPermitField(false);
  }, [shouldExpandOnScroll]);

  useEffect(() => {
    if (!canCreateAsCustomer) {
      setContractorSource("my_contractors");
    }
  }, [canCreateAsCustomer, setContractorSource]);

  // Clear the state when the component unmounts
  useEffect(() => {
    return () => {
      // Clear the state when the component unmounts
      if (attachingNewPermit?.started) {
        dispatch(
          setAttachingNewPermit({
            started: false,
            taskId: "",
            form: {},
            county: "",
            city: "",
            addedPermits: [],
            additionalPermits: {},
            usedContractors: [],
            packageSnapshot: null,
            prevPermitGrid: {
              page: 0,
              pageSize: 5,
              rowCount: 0,
            },
          }),
        );
      }
    };
  }, [attachingNewPermit?.started, dispatch]);

  // keep ref updated
  useEffect(() => {
    selectedFormsRef.current = props?.selectedForms;
  }, [props?.selectedForms]);

  useEffect(() => {
    if (
      isOpen &&
      currentSession === "subUserSession" &&
      props?.page === 1 &&
      contractors?.length === 0
    ) {
      setIsOpen(false);
    }
  }, [currentSession, props?.page, isOpen, contractors, setIsOpen]);

  const {
    county,
    selectedCity,
    selectedForms,
    setAvailableForms,
    setGroupedForms,
    setSelectedForms,
    setDisplayImage,
    displayImage,
    setCities,
    selectedPermitType,
    availableForms,
    stormwaterSelection,
  } = props;

  useEffect(() => {
    const countyData =
      availablePermitForms.find((item) => item[county])?.[county] || {};

    const city = selectedCity
      ? selectedCity?.toLowerCase().replace(/ /g, "_")
      : "";

    if (countyData?.noc_by_city?.[city]) {
      setDisplayImage(
        countyData?.noc_by_city?.[city]?.img ||
          countyData?.noc_by_city?.[city]?.image_urls,
      );
    } else {
      setDisplayImage(countyData?.noc?.img || countyData?.noc?.image_urls);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [county, availablePermitForms, selectedCity]);

  useEffect(() => {
    // In additional permit flow, ensure NOC and NOC Affidavit are not part of the selectedForms
    if (attachingNewPermit?.started && Array.isArray(selectedForms)) {
      if (
        selectedForms.includes(nocId) ||
        selectedForms.includes("noc_affidavit")
      ) {
        setSelectedForms((prev) =>
          prev.filter((name) => name !== nocId && name !== "noc_affidavit"),
        );
      }
    }
  }, [attachingNewPermit?.started, selectedForms, setSelectedForms, nocId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const countyExists = availablePermitForms.some((item) => item[county]);

      if (countyExists) {
        setCities(getCitiesForCounty(availablePermitForms, county));

        const isPrivateProvider = currentSession === "privateProviderSession";

        const forms = getCombinedList(
          availablePermitForms,
          county,
          selectedCity,
          selectedFormsRef.current, // use ref here
          setSelectedForms,
          attachingNewPermit?.started ? [] : null, // Pass [] for additional permits to prevent auto-adding NOC
          formsOrder,
          availablePermitTypes,
          selectedPermitType,
          isPrivateProvider,
          permitTypeFormMap,
        );

        setNocId(forms?.nocId);

        setAvailableForms(forms?.permits);
        setGroupedForms(forms?.groupedPermits || null);

        const containsFloridaTemplates = displayImage?.some((img) =>
          img.includes("florida-templates"),
        );
        // Prevent overriding signed NOC with template during additional permit flow
        if (containsFloridaTemplates && !attachingNewPermit?.started) {
          setDisplayImage(forms?.nocImg);
        }
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [
    county,
    selectedCity,
    setAvailableForms,
    setGroupedForms,
    setSelectedForms,
    setDisplayImage,
    displayImage,
    setCities,
    availablePermitForms,
    formsOrder,
    availablePermitTypes,
    selectedPermitType,
    currentSession,
    attachingNewPermit?.started,
    setNocId,
    permitTypeFormMap,
  ]);

  useEffect(() => {
    if (!selectedForms?.length) return;

    const lastFormName = selectedForms[selectedForms.length - 1];
    const lastForm = availableForms.find(
      (form) => form.form_id === lastFormName,
    );
    if (!lastForm) return;

    let updatedImageUrls;

    if (lastForm.name === "stormwater") {
      const matchedStorm = lastForm.forms?.find(
        (form) => form.name === stormwaterSelection,
      );
      updatedImageUrls = matchedStorm?.img || matchedStorm?.image_urls;
    } else {
      updatedImageUrls = lastForm?.img || lastForm?.image_urls;
    }

    // Do not override signed NOC preview during additional permit flow when last selected is noc
    // But allow other forms to display their images
    if (
      attachingNewPermit?.started &&
      (lastFormName === nocId || lastForm?.form_id === nocId)
    ) {
      return;
    }

    const newString = JSON.stringify(updatedImageUrls);
    const oldString = JSON.stringify(displayImageRef.current);

    if (updatedImageUrls && newString !== oldString) {
      displayImageRef.current = updatedImageUrls; // update ref manually
      setDisplayImage(updatedImageUrls);
    }
  }, [
    selectedForms,
    availableForms,
    stormwaterSelection,
    setDisplayImage,
    attachingNewPermit?.started,
    nocId,
  ]);

  useEffect(() => {
    document.addEventListener("contextmenu", function (e) {
      e.preventDefault();
    });
  }, []);

  const packageNameTextfieldContent = () => (
    <>
      <Typography
        align="center"
        px={1}
        pb={1}
        mt={1}
        variant="body2"
        fontWeight="bold"
        sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
      >
        The permit package name will be used as your project name and may be
        uploaded to the city permitting system if automatic permit number
        generation is enabled.
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", pb: 1 }}>
        <TextField
          // fullWidth
          // helperText={Boolean(formik.touched.zip) && formik.errors.zip}
          label="Project & Permit Package Name"
          name="ppName"
          // onBlur={formik.handleBlur}
          onChange={(e) => {
            props?.setPpName(e.target.value);
          }}
          type="zip"
          value={props?.ppName}
          size="small"
        />
      </Box>
    </>
  );

  return (
    <Box>
      <Typography
        px={2}
        color="primary"
        align="center"
        mt={1}
        sx={{
          fontSize: { xs: "1.5rem", sm: "1.9rem", md: "2.2rem" },
          fontWeight: "bold",
          mb: headerCondensed ? 0.5 : 0,
        }}
      >
        {attachingNewPermit?.started
          ? "Attach an Additional Permit"
          : "Create Your Permit Package"}
      </Typography>
      <Typography
        align="center"
        color="primary"
        sx={{
          fontSize: { xs: "1rem", sm: "1.4rem" },
          fontWeight: "bold",
          mt: -1,
        }}
      >
        {props?.county} County
      </Typography>
      <Box
        ref={descriptionTextRef}
        sx={{
          overflow: "hidden",
          transition: "all 240ms ease",
          maxHeight: headerCondensed ? 0 : 120,
          opacity: headerCondensed ? 0 : 1,
          transform: headerCondensed ? "translateY(-6px)" : "translateY(0)",
        }}
      >
        <Typography
          variant="body2"
          px={2}
          color="primary"
          align="center"
          mt={0}
          sx={{ fontSize: { xs: "0.78rem", sm: "0.95rem" } }}
        >
          {props?.page === 0
            ? attachingNewPermit?.started
              ? "Select the permit type and forms to add. Notice of Commencement and Affidavit of NOC Filing are already created and not needed again."
              : "Choose the forms you want to create for your permit package. Once created, the package may need to be notarized and/or recorded."
            : props?.page === 1 &&
              (attachingNewPermit?.started
                ? "Select a contractor that hasn't been used on this package."
                : "If you have previously added more than one contractor, you can select which contractor will be listed on the documents and which one will sign them.")}
        </Typography>
      </Box>
      {props?.page === 1 && (
        <Box ref={projectNameFieldRef}>
          {isMediumDown ? (
            <Accordion
              expanded={expandedPermitField}
              onChange={(_, isExpanded) => {
                setExpandedPermitField(isExpanded);
              }}
              slotProps={{
                root: {
                  sx: {
                    borderRadius: "8px !important",
                    margin: "8px 16px 16px 16px !important",
                    boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.2) !important",
                  },
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="permit-form-content"
                id="permit-form-header"
                sx={{ fontSize: "14px", fontWeight: 600 }}
              >
                Project & Permit Package Name
              </AccordionSummary>
              <AccordionDetails sx={{ pb: "0px !important" }}>
                {packageNameTextfieldContent()}
              </AccordionDetails>
            </Accordion>
          ) : (
            <Box
              sx={{
                border: "2px solid #d3d3d3",
                boxShadow: "6px 1px 16px 1px rgba(0, 0, 0, 0.3)",
                mx: 2,
                p: 1,
                mt: 1,
                borderRadius: "20px",
              }}
            >
              {packageNameTextfieldContent()}
            </Box>
          )}
        </Box>
      )}
      <Box sx={{ px: 2, mt: props?.page === 1 ? 2 : 0 }}>
        <Box
          sx={{
            overflow: "hidden",
            transition: "all 240ms ease",
            maxHeight: headerCondensed ? 0 : 48,
            opacity: headerCondensed ? 0 : 1,
            transform: headerCondensed ? "translateY(-6px)" : "translateY(0)",
          }}
        >
          <Typography
            align="center"
            mt={0}
            mb={0}
            sx={{
              fontSize: { xs: "0.95rem", sm: "1.1rem" },
              fontWeight: 700,
            }}
          >
            {(() => {
              if (props?.page === 0) return "Select the Permit Type First";

              if (props?.page === 1) {
                // Check if this is Generator Permit in St. Johns County
                const isGeneratorPermitStJohns =
                  props?.selectedPermitType === "generator_permit" &&
                  (county?.toLowerCase() === "st_johns" ||
                    county?.toLowerCase() === "st johns" ||
                    county?.toLowerCase() === "st. johns");

                // Don't show title for Generator Permit in St. Johns
                if (isGeneratorPermitStJohns) return "";

                if (
                  canCreateAsCustomer &&
                  contractorSource === "customer_contractors"
                ) {
                  return "Which Customer Should Own This Permit Package?";
                }

                // Show contractor titles for other permits
                if (contractors?.length > 1)
                  return "Who Will Be The Contractor?";
                if (contractors?.length === 0) return "No Contractor Found";
              }

              return "";
            })()}
          </Typography>
        </Box>

        {props?.page === 1 && canCreateAsCustomer && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 0.4,
            }}
          >
            <Tabs
              value={contractorSource}
              onChange={(_, value) => setContractorSource(value)}
              variant="fullWidth"
              sx={{
                width: "100%",
                maxWidth: 520,
                minHeight: 42,
                backgroundColor: "#f7f7fb",
                borderRadius: 999,
                p: 0.5,
                "& .MuiTabs-indicator": {
                  display: "none",
                },
              }}
            >
              <Tab
                value="my_contractors"
                label="My Contractors"
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  color: "text.primary",
                  "&.Mui-selected": {
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  },
                }}
              />
              <Tab
                value="customer_contractors"
                label="Customer’s Contractors"
                sx={{
                  minHeight: 42,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  color: "text.primary",
                  "&.Mui-selected": {
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  },
                }}
              />
            </Tabs>
          </Box>
        )}

        {props?.page === 0 && (
          <SelectFormsStep
            selectedPermitType={selectedPermitType}
            setSelectedPermitType={props?.setSelectedPermitType}
            selectedForms={selectedForms}
            setSelectedForms={setSelectedForms}
            stormwaterSelection={props?.stormwaterSelection}
            setStormwaterSelection={props?.setStormwaterSelection}
            availableForms={props?.availableForms}
            setDisplayImage={setDisplayImage}
            cities={props?.cities}
            selectedCity={selectedCity}
            setSelectedCity={props?.setSelectedCity}
            countyLoading={props?.countyLoading}
            groupedForms={props?.groupedForms}
            privateProviderSignerType={props?.privateProviderSignerType}
            setPrivateProviderSignerType={props?.setPrivateProviderSignerType}
            contractors={contractors}
            formContractors={formContractors}
            setFormContractors={setFormContractors}
            county={county}
            isPP={isPP}
            page={props?.page}
            headerCondensed={headerCondensed}
            onCardsScroll={(isCondensed) => {
              if (isMobile) {
                setHeaderCondensed(isCondensed);
              }
            }}
            nocId={nocId}
            fetchCountyPermit={fetchCountyPermit}
          />
        )}

        {props?.page === 1 &&
          (() => {
            // Check if this is Generator Permit in St. Johns County
            const isGeneratorPermitStJohns =
              props?.selectedPermitType === "generator_permit" &&
              (county?.toLowerCase() === "st_johns" ||
                county?.toLowerCase() === "st johns" ||
                county?.toLowerCase() === "st. johns");

            // Show email configuration for Generator Permit in St. Johns
            if (isGeneratorPermitStJohns) {
              return (
                <GeneratorPermitEmailStep
                  autoEmailEnabled={autoEmailEnabled}
                  setAutoEmailEnabled={setAutoEmailEnabled}
                  cityEmail={cityEmail}
                  setCityEmail={setCityEmail}
                  responseEmail={responseEmail}
                  setResponseEmail={setResponseEmail}
                  emailRows={emailRows}
                  setEmailRows={setEmailRows}
                  userEmail={userEmail}
                  emailRowOnError={emailRowOnError}
                  setEmailRowOnError={setEmailRowOnError}
                />
              );
            }

            // Show contractor selection for all other cases
            if (
              canCreateAsCustomer &&
              contractorSource === "customer_contractors"
            ) {
              return (
                <SelectCustomerUserStep
                  selectedCustomerUser={selectedCustomerUser}
                  setSelectedCustomerUser={setSelectedCustomerUser}
                  selectedCustomerContractor={selectedCustomerContractor}
                  setSelectedCustomerContractor={setSelectedCustomerContractor}
                  onCardsScroll={(isCondensed) => {
                    if (isMobile) {
                      setHeaderCondensed(isCondensed);
                    }
                  }}
                  setShouldExpandOnScroll={setShouldExpandOnScroll}
                  descriptionTextRef={descriptionTextRef}
                  projectNameFieldRef={projectNameFieldRef}
                />
              );
            }

            return (
              <SelectContractorStep
                contractorIndex={props?.contractorIndex}
                setContractorIndex={props?.setContractorIndex}
                page={props?.page}
                selectedCity={selectedCity}
                selectedForms={selectedForms}
                availableForms={props?.availableForms}
                county={county}
                displayImage={displayImage}
                cities={props?.cities}
                contractors={contractors}
                isPP={isPP}
                favoritesOnly={favoritesOnly}
                setFavoritesOnly={setFavoritesOnly}
                onCardsScroll={(isCondensed) => {
                  if (isMobile) {
                    setHeaderCondensed(isCondensed);
                  }
                }}
                setShouldExpandOnScroll={setShouldExpandOnScroll}
                descriptionTextRef={descriptionTextRef}
                projectNameFieldRef={projectNameFieldRef}
              />
            );
          })()}

        {props?.page === 2 &&
          props?.county === "Duval" &&
          selectedCity.toLowerCase() === "jacksonville" &&
          contractors?.length > 0 && (
            <SetupJaxDetails
              connectToCity={props?.connectToCity}
              setConnectToCity={props?.setConnectToCity}
              selectedCity={selectedCity}
              county={county}
              selectedCityUser={props?.selectedCityUser}
              setSelectedCityUser={props?.setSelectedCityUser}
              addNewUser={props?.addNewUser}
              setAddNewUser={props?.setAddNewUser}
              selectUserLoading={selectUserLoading}
              setSelectUserLoading={setSelectUserLoading}
              selectedForms={selectedForms}
              isNocSelected={selectedForms.includes(nocId)}
            />
          )}
      </Box>
    </Box>
  );
};

export default SelectPermitPackageForms;
